import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchInitialSiteInfo } from '../../store/financialSlice';

import SearchIcon from '@mui/icons-material/Search';
import PropTypes from 'prop-types';
import AccountStatus from './AccountStatus';
import ViewableSites from './ViewableSites';
import { Loader } from '../common/Loader';
import BlockUserModal from './BlockUserModal';
import EditUserModal from './EditUserModal';

import {
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  useTheme,
} from '@mui/material';
import EditOptions from './EditOptions';
import ExportOptions from './ExportOptions';

export default function UsersTable({ portalUsers, isLoading, error }) {
  const [searchText, setSearchText] = useState('');
  const [hoveredRow, setHoveredRow] = useState(null);
  const [showBlockUserModal, setShowBlockUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [userToEdit, setUserToEdit] = useState();
  const dispatch = useDispatch();
  const theme = useTheme();

  const { portfolioFinancialData } = useSelector((state) => state.sites);

  useEffect(() => {
    if (!portfolioFinancialData) {
      dispatch(fetchInitialSiteInfo());
    }
  }, [dispatch, portfolioFinancialData]);

  const siteNames = portfolioFinancialData?.sites.map((site) => ({
    id: site.id,
    name: site.siteName,
  }));

  const handleMouseEnter = (index) => {
    setHoveredRow(index);
  };

  const handleMouseLeave = () => {
    setHoveredRow(null);
  };

  const filteredRows = portalUsers.filter((user) =>
    Object.values(user).some((val) => String(val).toLowerCase().includes(searchText.toLowerCase())),
  );

  const formatAccountType = (accountType) => {
    return accountType === 'admin' ? 'Portfolio Manager' : 'Site Manager';
  };

  if (isLoading || !portfolioFinancialData) {
    return <Loader />;
  }

  if (error) {
    return (
      <TableContainer
        sx={{
          marginTop: '48px',
          borderRadius: '15px',
          marginBottom: '16px',
          padding: '32px',
          display: 'flex',
          justifyContent: 'center',
        }}
        component={Paper}
      >
        <p className="text-red-700">Something went wrong. Please try again or contact support</p>
      </TableContainer>
    );
  }

  return (
    <>
      <TableContainer
        sx={{ marginTop: '48px', borderRadius: '15px', marginBottom: '16px', padding: '32px' }}
        component={Paper}
      >
        <div className="flex justify-between items-center pb-[32px]">
          <div>
            <h1 className="font-bold">Users</h1>
          </div>
          <div>
            <TextField
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '100px',
                },
              }}
              placeholder="Search Table"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: theme.palette.button.secondary }} />
                  </InputAdornment>
                ),
              }}
              variant="outlined"
            />
          </div>
          <div>
            <ExportOptions users={filteredRows} siteNames={siteNames} />
          </div>
        </div>

        <Table
          sx={{
            borderCollapse: 'separate',
            borderRadius: '15px',
            border: '1px solid rgb(224, 224, 224)',
          }}
          aria-label="simple table"
        >
          <TableHead>
            <TableRow key="table-header">
              <TableCell>
                <p className="font-bold">Email</p>
              </TableCell>
              <TableCell align="right">
                <p className="font-bold">First Name</p>
              </TableCell>
              <TableCell align="right">
                <p className="font-bold">Last Name</p>
              </TableCell>
              <TableCell align="right">
                <p className="font-bold">Account Type</p>
              </TableCell>
              <TableCell align="right">
                <p className="font-bold">Viewable Sites</p>
              </TableCell>
              <TableCell align="right">
                <p className="font-bold">Account Status</p>
              </TableCell>
              <TableCell align="center"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.map((row, index) => (
              <TableRow
                onMouseEnter={() => handleMouseEnter(index)}
                onMouseLeave={handleMouseLeave}
                key={row.email}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                hover
              >
                <TableCell component="th" scope="row">
                  {row.email}
                </TableCell>
                <TableCell align="right">{row.firstName}</TableCell>
                <TableCell align="right">{row.lastName}</TableCell>
                <TableCell align="right">{formatAccountType(row.accountType)}</TableCell>
                <TableCell align="right">
                  <ViewableSites
                    viewableSitesIds={row.viewableSites}
                    accountStatus={row.accountStatus}
                    siteNames={siteNames}
                  />
                </TableCell>
                <TableCell align="right">
                  <AccountStatus accountStatus={row.accountStatus} />
                </TableCell>
                <TableCell align="right">
                  <Box
                    sx={{
                      visibility: hoveredRow === index ? 'visible' : 'hidden',
                    }}
                  >
                    <EditOptions
                      user={row}
                      setUserToEdit={setUserToEdit}
                      setShowBlockUserModal={setShowBlockUserModal}
                      setShowEditUserModal={setShowEditUserModal}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {showBlockUserModal && (
        <BlockUserModal user={userToEdit} setShowBlockUserModal={setShowBlockUserModal} />
      )}
      {showEditUserModal && (
        <EditUserModal user={userToEdit} setShowEditUserModal={setShowEditUserModal} />
      )}
    </>
  );
}

UsersTable.propTypes = {
  portalUsers: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.any,
};
